import React, { ChangeEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, FormikErrors, FormikProps, useFormikContext, withFormik } from 'formik'
import { t } from 'i18next'

import { Button } from '../../global/button/Button'
import { useArabicSessionLanguage } from '../../global/context/SessionSettingsContext'
import IconButton from '../../global/iconButton/IconButton'
import { BackIcon } from '../../icons/BackIcon'
import { ForwardIcon } from '../../icons/ForwardIcon'
import { AccountDetailedDto } from '../../model/AccountDetailedDto'
import { DocumentCategorySizeDto } from '../../model/DocumentCategories'
import { AppropriatenessTestTakeTestFormValues } from '../../model/Test'
import {
  isTestQuestionWidgetRadio,
  isTestQuestionWidgetSelect,
} from '../../model/TestQuestionWidget'
import {
  TestSectionDto,
  TestSectionQuestionDto,
  TestSectionsDto,
  WidgetOptions,
  isAppropriatenessTestPersonalInformation,
  isAppropriatenessTestTradingKnowledge,
} from '../../model/TestSectionsDto'
import {
  TestRadioWidget,
  TestSelectWidget,
  TestTextWidget,
} from '../../pages/Signup/Pages/FinancialInfo/FinancialInfoStep1234Form'
import { getCleanedAnswers, showConditionalQuestions } from '../../ui/TestFields/TestFields'
import { TextH3 } from '../../ui/Typography/Typography'
import { useCategorySizesReadContext } from '../../utils/CategorySizesContext'
import { scrollToTop } from '../../utils/useScrollToTop'

import styles from './AppropriatenessTestTakeTestForm.module.scss'

export const AppropriatenessTestTakeTestFormUI: React.FC<
  FormikProps<AppropriatenessTestTakeTestFormValues> & OuterProps
> = (props) => {
  const {
    data,
    defaultTestSections,
    combineAllTests,
    noPreviousStep,
    step,
    isSubmitting,
    errors,
    handleSubmit,
    setFieldTouched,
    setErrors,
    isValid,
    validateForm,
  } = props

  const [isValidating, setIsValidating] = useState(false)
  const { categorySizes } = useCategorySizesReadContext()

  const getETDSection = data.sections?.find(
    (appTest) => appTest?.code === 'futures_investment_knowledge'
  )

  const dataSections = combineAllTests ? defaultTestSections : data?.sections
  const section = dataSections?.find((x) => x.code === step)
  const isBackButtonAvailable =
    !noPreviousStep && dataSections && !isFirstSection(dataSections, step)
  const { t } = useTranslation()

  const context = useFormikContext<AppropriatenessTestTakeTestFormValues>()

  useEffect(() => {
    setIsValidating(true)
    validateForm(context.values).then(() => {
      setIsValidating(false)
    })
  }, [context.values, validateForm])

  const handleSubmitForm = (event: ChangeEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (Object.keys(errors).length > 0) {
      setFieldTouchedAll()
      scrollToTop()
    } else {
      handleSubmit()
    }
  }

  const handleBack = () => {
    setErrors({})
    props.goPrevStep()
  }

  const setFieldTouchedAll = () => {
    if (context.values.selectedAnswers) {
      Object.keys(context.values.selectedAnswers).forEach((key) => {
        setFieldTouched(`selectedAnswers.${key}`, true)
      })
    }

    if (context.values.freeAnswers) {
      Object.keys(context.values.freeAnswers).forEach((key) => {
        setFieldTouched(`freeAnswers.${key}`, true)
      })
    }

    Object.entries(errors).forEach(([key, value]) => {
      if (Object.keys(value).length > 0) {
        Object.entries(value).forEach(([key2]) => {
          setFieldTouched(`${key}.${key2}`, true)
        })
      }
    })
  }

  const isArabic = useArabicSessionLanguage()

  return (
    <div className='pb-3 mt-3'>
      <Form onSubmit={handleSubmitForm}>
        {section && (
          <div className={styles.wrapper}>
            <TextH3 className='is-flex mb-3'>
              {isBackButtonAvailable && (
                <span className='is-flex is-align-items-center mr-2'>
                  <IconButton onClick={handleBack}>
                    {isArabic ? <ForwardIcon /> : <BackIcon />}
                  </IconButton>
                </span>
              )}
              <span>{section.name}</span>
            </TextH3>
            {section.questions.map((x) => (
              <QuestionFieldFactory
                {...x}
                values={context.values}
                categorySizes={categorySizes}
                section={section}
                key={x.question.id}
              />
            ))}

            <>
              {combineAllTests &&
                section?.code === 'cfd_investment_knowledge' &&
                getETDSection?.questions && (
                  <>
                    <div className='pb-3'>
                      <TextH3 className='is-flex mb-3'>
                        <span>{getETDSection?.name}</span>
                      </TextH3>
                    </div>
                    {getETDSection.questions?.map((x) => (
                      <QuestionFieldFactory
                        {...x}
                        categorySizes={categorySizes}
                        values={context.values}
                        section={section}
                        key={x.question.id}
                      />
                    ))}
                  </>
                )}
            </>
          </div>
        )}
        <div>
          <Button
            appearance='primary'
            size='L'
            type='submit'
            disabled={isSubmitting || !isValid || isValidating}
            loading={isSubmitting || isValidating}
            className={styles.button}
          >
            {dataSections && isLastSection(dataSections, step) ? t('Submit') : t('Next')}
          </Button>
        </div>
      </Form>
    </div>
  )
}

interface QuestionFieldFactoryProps extends TestSectionQuestionDto {
  section: TestSectionDto
  values: AppropriatenessTestTakeTestFormValues
  categorySizes?: DocumentCategorySizeDto[]
}

const QuestionFieldFactory: React.FC<QuestionFieldFactoryProps> = (props) => {
  const { question, widget, values, categorySizes } = props

  if (isOptionsHide(widget.widgetOptions)) {
    return null
  }

  if (isTestQuestionWidgetSelect(question.widgetType)) {
    return <TestSelectWidget {...props} categorySizes={categorySizes} />
  }

  if (isTestQuestionWidgetRadio(question.widgetType)) {
    return <TestRadioWidget {...props} categorySizes={categorySizes} />
  }

  if (!isFieldShow(props, values)) {
    return null
  }

  return <TestTextWidget {...props} />
}

interface OuterProps {
  data: TestSectionsDto
  step: string
  account: AccountDetailedDto | undefined
  values: AppropriatenessTestTakeTestFormValues | undefined
  combineAllTests?: boolean
  noPreviousStep?: boolean
  defaultTestSections?: TestSectionDto[]
  onSubmit(values: AppropriatenessTestTakeTestFormValues): void
  goPrevStep(): void
  goNextStep(values: AppropriatenessTestTakeTestFormValues): void
}

const dataToSubmit = (
  values: AppropriatenessTestTakeTestFormValues,
  sections: readonly TestSectionDto[]
): AppropriatenessTestTakeTestFormValues => {
  const { selectedAnswers: cleanSelectedAnswers, freeAnswers: cleanFreeAnswers } =
    getCleanedAnswers(values.selectedAnswers, values.freeAnswers, sections)

  return {
    ...values,
    selectedAnswers: cleanSelectedAnswers,
    freeAnswers: cleanFreeAnswers,
  }
}

export const AppropriatenessTestTakeTestForm = withFormik<
  OuterProps,
  AppropriatenessTestTakeTestFormValues
>({
  mapPropsToValues: ({ data, account, values }) => {
    return {
      category: data?.category || '',
      clientId: account?.id || '',
      testId: data?.id || '',
      dateTaken: new Date().toISOString(),
      selectedAnswers: values?.selectedAnswers || {},
      freeAnswers: values?.freeAnswers || {},
      documents: values?.documents || {},
    }
  },
  validate: (values, props) => {
    const errors: FormikErrors<AppropriatenessTestTakeTestFormValues> = {}
    const currentSection = props.data.sections.find((x) => x.code === props.step)

    currentSection?.questions
      .filter(({ widget }) =>
        showConditionalQuestions(
          currentSection?.questions,
          Object.values(values.selectedAnswers ?? {}).flatMap((value) => value),
          widget
        )
      )
      .forEach((question) => {
        const options = question.widget.widgetOptions
        const answer =
          values.freeAnswers?.[question.question.id] ??
          Object.values(values.selectedAnswers ?? {})
            .flatMap((value) => value)
            ?.find((x) => question.answers.some((answer) => answer.id === x))

        if ((question.isMandatory || question.widget.widgetOptions?.isMandatory) && !answer) {
          ;(errors[question.widget.name as keyof AppropriatenessTestTakeTestFormValues] as any) =
            t('Validation.Required')
        } else if (options?.regExp) {
          const regExp = new RegExp(options.regExp)
          if (answer && !regExp.test(answer.toString())) {
            ;(errors[question.widget.name as keyof AppropriatenessTestTakeTestFormValues] as any) =
              t(`Validation.Invalid format`)
          }
        }
      })

    return errors
  },
  handleSubmit: (values, { props, setSubmitting }) => {
    try {
      setSubmitting(true)

      const dataSections = props?.combineAllTests
        ? props?.defaultTestSections
        : props?.data?.sections

      if (dataSections && isLastSection(dataSections, props.step)) {
        props.onSubmit(dataToSubmit(values, props.data.sections))
      } else {
        props.goNextStep(dataToSubmit(values, props.data.sections))
      }

      setSubmitting(false)
    } finally {
      setSubmitting(false)
    }
  },
  enableReinitialize: true,
  validateOnMount: true,
  validateOnChange: true,
  validateOnBlur: true,
})(AppropriatenessTestTakeTestFormUI)

const isFirstSection = (sections: TestSectionDto[], step: string) => {
  const firstSection = sections[0]
  return firstSection.code === step
}

const isLastSection = (sections: TestSectionDto[], step: string) => {
  const lastSection = sections[sections.length - 1]
  return lastSection.code === step
}

const isFieldShow = (
  props: QuestionFieldFactoryProps,
  values: AppropriatenessTestTakeTestFormValues
) => {
  const { section, answers: arrayAnswers = [], widget } = props

  const options = widget.widgetOptions
  const answerWidgetKeys = Object.keys(values.selectedAnswers)

  if (isAppropriatenessTestPersonalInformation(section.code)) {
    const foundPersonalInfo = answerWidgetKeys.find((x) => x === options?.depend)
    return foundPersonalInfo
  }

  if (isAppropriatenessTestTradingKnowledge(section.code)) {
    const foundTradingKnowledgeKey = answerWidgetKeys.find((x) => x === options?.depend)

    const foundTradingKnowledgeValue =
      foundTradingKnowledgeKey && values.selectedAnswers?.[foundTradingKnowledgeKey]

    const foundTradingKnowledgeCode =
      foundTradingKnowledgeKey &&
      foundTradingKnowledgeValue &&
      arrayAnswers.find((x) => x?.id === foundTradingKnowledgeValue)?.code === '1'

    return foundTradingKnowledgeCode
  }

  return false
}

const isOptionsHide = (options: WidgetOptions | null) => {
  return options && 'hide' in options && 'depend' in options && options?.hide === true
}
